import React, { useEffect, useMemo, useState } from 'react';
import DataTable from '../components/ResponsiveDataTable';
import {
  getGstB2B,
  getGstB2BSezDe,
  getGstCDNR,
  getGstDetails,
  getGstDocs13,
  getGstHSN12,
} from '../config/api';
import { saveBlob } from '../utils/nativeFiles';
import './GstDetails.css';

const displayValue = value => (value === null || value === undefined || value === '' ? '—' : value);
const displayBlank = value => (value === null || value === undefined ? '' : value);
const formatAmount = value => (
  value === null || value === undefined || value === ''
    ? '—'
    : `₹${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
);

const COMMON_FILTER_DEFAULTS = { date_from: '', date_to: '' };
const TAB_FILTER_DEFAULTS = {
  details: { invoice_number: '', document_type: '' },
  b2b: { invoice_number: '', recipient_gstin: '' },
  cdnr: { invoice_number: '', recipient_gstin: '' },
  hsn12: { hsn_sac: '' },
  docs13: { invoice_number: '', nature: '' },
  b2bSezDe: { invoice_number: '', recipient_gstin: '' },
};
const TAB_FILTER_CONFIG = {
  details: [
    { name: 'invoice_number', label: 'Voucher number', placeholder: 'UP/AG/26-27/...' },
    {
      name: 'document_type',
      label: 'Document type',
      type: 'select',
      options: [
        { value: '', label: 'All documents' },
        { value: 'invoice', label: 'Invoices' },
        { value: 'credit_note', label: 'Credit notes' },
      ],
    },
  ],
  b2b: [
    { name: 'invoice_number', label: 'Invoice number', placeholder: 'UP/AG/26-27/...' },
    { name: 'recipient_gstin', label: 'Recipient GSTIN', placeholder: '09ABCDE1234F1Z5' },
  ],
  cdnr: [
    { name: 'invoice_number', label: 'Note number', placeholder: 'CN/AG/26-27/...' },
    { name: 'recipient_gstin', label: 'Recipient GSTIN', placeholder: '09ABCDE1234F1Z5' },
  ],
  hsn12: [
    { name: 'hsn_sac', label: 'HSN / SAC', placeholder: '85414300' },
  ],
  docs13: [
    { name: 'invoice_number', label: 'Series / document number', placeholder: 'UP/AG/26-27' },
    {
      name: 'nature',
      label: 'Nature of document',
      type: 'select',
      options: [
        { value: '', label: 'All document types' },
        { value: 'invoice', label: 'Invoices for outward supply' },
        { value: 'credit_note', label: 'Credit notes' },
      ],
    },
  ],
  b2bSezDe: [
    { name: 'invoice_number', label: 'Invoice number', placeholder: 'UP/AG/26-27/...' },
    { name: 'recipient_gstin', label: 'Recipient GSTIN', placeholder: '09ABCDE1234F1Z5' },
  ],
};
const createTabFilters = () => Object.fromEntries(
  Object.entries(TAB_FILTER_DEFAULTS).map(([tab, values]) => [tab, { ...values }])
);

const detailColumns = [
  { name: 'Sr', selector: row => row.sr, width: '64px', cell: row => <span className="gst-row-number">{row.sr}</span> },
  { name: 'Supplier State', selector: row => row.supplierState, width: '140px', sortable: true },
  { name: 'State Code', selector: row => row.stateCode, width: '100px', cell: row => <span className="gst-code-badge">{displayValue(row.stateCode)}</span> },
  {
    name: 'Doc Type',
    selector: row => row.docType,
    width: '118px',
    sortable: true,
    cell: row => <span className={`gst-doc-badge ${row.docType === 'Credit Note' ? 'credit' : 'invoice'}`}>{displayValue(row.docType)}</span>,
  },
  { name: 'Voucher No', selector: row => row.voucherNo, width: '175px', sortable: true, cell: row => <span className="gst-mono gst-voucher">{displayValue(row.voucherNo)}</span> },
  { name: 'Date', selector: row => row.date, width: '110px', sortable: true },
  { name: 'Orig. Invoice (CN)', selector: row => row.originalInvoice, width: '175px', cell: row => <span className="gst-mono gst-muted-value">{displayValue(row.originalInvoice)}</span> },
  { name: 'Party', selector: row => row.party, width: '200px', cell: row => <span className="gst-party-name" title={row.party}>{displayValue(row.party)}</span> },
  { name: 'Party GSTIN', selector: row => row.partyGstin, width: '165px', cell: row => <span className="gst-mono">{displayValue(row.partyGstin)}</span> },
  { name: 'POS (Books)', selector: row => row.posBooks, width: '130px' },
  { name: 'POS for Filing', selector: row => row.posFiling, width: '130px' },
  { name: 'Supply Type', selector: row => row.supplyType, width: '125px', cell: row => <span className={`gst-supply-badge ${row.supplyType === 'Inter-State' ? 'inter' : 'intra'}`}>{displayValue(row.supplyType)}</span> },
  { name: 'GSTR-1 Table', selector: row => row.gstrTable, width: '115px', cell: row => <span className="gst-table-badge">{displayValue(row.gstrTable)}</span> },
  { name: 'SKU ID', selector: row => row.skuId, width: '145px', cell: row => <span className="gst-sku-chip">{displayValue(row.skuId)}</span> },
  { name: 'Item / Description', selector: row => row.description, width: '220px', cell: row => <span className="gst-description" title={row.description}>{displayValue(row.description)}</span> },
  { name: 'HSN/SAC for Filing', selector: row => row.hsnSac, width: '145px', cell: row => <span className="gst-mono">{displayValue(row.hsnSac)}</span> },
  { name: 'Qty', selector: row => row.qty, width: '75px', right: true, cell: row => <span className="gst-number-cell">{displayValue(row.qty)}</span> },
  { name: 'Pre-GST Discount', selector: row => row.preGstDiscount, width: '145px', right: true, cell: row => <span className="gst-discount-value">{formatAmount(row.preGstDiscount)}</span> },
  { name: 'Taxable Value for GST', selector: row => row.taxableValue, width: '165px', right: true, cell: row => <span className="gst-money emphasized">{formatAmount(row.taxableValue)}</span> },
  { name: 'Rate %', selector: row => row.rate, width: '85px', right: true, cell: row => <span className="gst-rate">{row.rate === null || row.rate === undefined ? '—' : `${row.rate}%`}</span> },
  { name: 'IGST', selector: row => row.igst, width: '100px', right: true, cell: row => <span className="gst-tax-value igst">{formatAmount(row.igst)}</span> },
  { name: 'CGST', selector: row => row.cgst, width: '100px', right: true, cell: row => <span className="gst-tax-value cgst">{formatAmount(row.cgst)}</span> },
  { name: 'SGST', selector: row => row.sgst, width: '100px', right: true, cell: row => <span className="gst-tax-value sgst">{formatAmount(row.sgst)}</span> },
  { name: 'Total Tax', selector: row => row.totalTax, width: '120px', right: true, cell: row => <span className="gst-money">{formatAmount(row.totalTax)}</span> },
  { name: 'Post-GST Discount', selector: row => row.postGstDiscount, width: '150px', right: true, cell: row => <span className="gst-discount-value">{formatAmount(row.postGstDiscount)}</span> },
  { name: 'Total Value', selector: row => row.totalValue, width: '130px', right: true, cell: row => <span className={`gst-total-cell${Number(row.totalValue) < 0 ? ' negative' : ''}`}>{formatAmount(row.totalValue)}</span> },
  { name: 'Key Vch|Rate', selector: row => row.voucherRateKey, width: '180px', cell: row => <span className="gst-mono gst-key">{displayValue(row.voucherRateKey)}</span> },
  { name: 'Key State|HSN|Rate', selector: row => row.stateHsnRateKey, width: '190px', cell: row => <span className="gst-mono gst-key">{displayValue(row.stateHsnRateKey)}</span> },
];

const b2bColumns = [
  { name: 'Sr', selector: row => row.sr, width: '64px', cell: row => <span className="gst-row-number">{row.sr}</span> },
  { name: 'Supplier State', selector: row => row.supplierState, width: '140px', sortable: true },
  { name: 'Supplier GSTIN', selector: row => row.supplierGstin, width: '165px', cell: row => <span className="gst-mono">{displayValue(row.supplierGstin)}</span> },
  { name: 'GSTIN/UIN Recipient', selector: row => row.recipientGstin, width: '175px', cell: row => <span className="gst-mono">{displayValue(row.recipientGstin)}</span> },
  { name: 'Receiver Name', selector: row => row.receiverName, width: '205px', cell: row => <span className="gst-party-name" title={row.receiverName}>{displayValue(row.receiverName)}</span> },
  { name: 'Invoice Number', selector: row => row.invoiceNumber, width: '175px', sortable: true, cell: row => <span className="gst-mono gst-voucher">{displayValue(row.invoiceNumber)}</span> },
  { name: 'Invoice Date', selector: row => row.invoiceDate, width: '110px', sortable: true },
  { name: 'Invoice Value', selector: row => row.invoiceValue, width: '130px', right: true, cell: row => <span className="gst-money emphasized">{formatAmount(row.invoiceValue)}</span> },
  { name: 'Place of Supply', selector: row => row.placeOfSupply, width: '145px', cell: row => <span className="gst-code-badge">{displayValue(row.placeOfSupply)}</span> },
  { name: 'Reverse Charge', selector: row => row.reverseCharge, width: '115px', cell: row => <span className="gst-static-badge">{displayValue(row.reverseCharge)}</span> },
  { name: 'Applicable % Tax Rate', selector: row => row.applicableTaxRate, width: '155px', right: true, cell: row => displayValue(row.applicableTaxRate) },
  { name: 'Invoice Type', selector: row => row.invoiceType, width: '145px', cell: row => <span className="gst-doc-badge invoice">{displayValue(row.invoiceType)}</span> },
  { name: 'E-Commerce GSTIN', selector: row => row.ecommerceGstin, width: '155px', cell: row => <span className="gst-mono gst-muted-value">{displayValue(row.ecommerceGstin)}</span> },
  { name: 'Rate', selector: row => row.rate, width: '80px', right: true, cell: row => <span className="gst-rate">{row.rate === null || row.rate === undefined ? '—' : `${row.rate}%`}</span> },
  { name: 'Taxable Value', selector: row => row.taxableValue, width: '135px', right: true, cell: row => <span className="gst-money emphasized">{formatAmount(row.taxableValue)}</span> },
  { name: 'Cess Amount', selector: row => row.cessAmount, width: '115px', right: true, cell: row => <span className="gst-money">{formatAmount(row.cessAmount)}</span> },
  { name: 'IGST', selector: row => row.igst, width: '105px', right: true, cell: row => <span className="gst-tax-value igst">{formatAmount(row.igst)}</span> },
  { name: 'CGST', selector: row => row.cgst, width: '105px', right: true, cell: row => <span className="gst-tax-value cgst">{formatAmount(row.cgst)}</span> },
  { name: 'SGST', selector: row => row.sgst, width: '105px', right: true, cell: row => <span className="gst-tax-value sgst">{formatAmount(row.sgst)}</span> },
];

const cdnrColumns = [
  { name: 'Sr', selector: row => row.sr, width: '64px', cell: row => <span className="gst-row-number">{row.sr}</span> },
  { name: 'Supplier State', selector: row => row.supplierState, width: '140px', sortable: true },
  { name: 'Supplier GSTIN', selector: row => row.supplierGstin, width: '165px', cell: row => <span className="gst-mono">{displayValue(row.supplierGstin)}</span> },
  { name: 'GSTIN/UIN of Recipient', selector: row => row.recipientGstin, width: '185px', cell: row => <span className="gst-mono">{displayValue(row.recipientGstin)}</span> },
  { name: 'Receiver Name', selector: row => row.receiverName, width: '205px', cell: row => <span className="gst-party-name" title={row.receiverName}>{displayValue(row.receiverName)}</span> },
  { name: 'Note Number', selector: row => row.noteNumber, width: '175px', sortable: true, cell: row => <span className="gst-mono gst-voucher">{displayValue(row.noteNumber)}</span> },
  { name: 'Note Date', selector: row => row.noteDate, width: '110px', sortable: true },
  { name: 'Note Type', selector: row => row.noteType, width: '95px', cell: row => <span className="gst-doc-badge credit">{displayValue(row.noteType)}</span> },
  { name: 'Place of Supply', selector: row => row.placeOfSupply, width: '145px', cell: row => <span className="gst-code-badge">{displayValue(row.placeOfSupply)}</span> },
  { name: 'Reverse Charge', selector: row => row.reverseCharge, width: '115px', cell: row => <span className="gst-static-badge">{displayValue(row.reverseCharge)}</span> },
  { name: 'Note Supply Type', selector: row => row.noteSupplyType, width: '145px', cell: row => <span className="gst-doc-badge invoice">{displayValue(row.noteSupplyType)}</span> },
  { name: 'Note Value', selector: row => row.noteValue, width: '125px', right: true, cell: row => <span className="gst-money emphasized">{formatAmount(row.noteValue)}</span> },
  { name: 'Applicable % of Tax Rate', selector: row => row.applicableTaxRate, width: '165px', right: true, cell: row => displayValue(row.applicableTaxRate) },
  { name: 'Rate', selector: row => row.rate, width: '80px', right: true, cell: row => <span className="gst-rate">{row.rate === null || row.rate === undefined ? '—' : `${row.rate}%`}</span> },
  { name: 'Taxable Value', selector: row => row.taxableValue, width: '135px', right: true, cell: row => <span className="gst-money emphasized">{formatAmount(row.taxableValue)}</span> },
  { name: 'Cess Amount', selector: row => row.cessAmount, width: '115px', right: true, cell: row => <span className="gst-money">{formatAmount(row.cessAmount)}</span> },
  { name: 'IGST', selector: row => row.igst, width: '105px', right: true, cell: row => <span className="gst-tax-value igst">{formatAmount(row.igst)}</span> },
  { name: 'CGST', selector: row => row.cgst, width: '105px', right: true, cell: row => <span className="gst-tax-value cgst">{formatAmount(row.cgst)}</span> },
  { name: 'SGST', selector: row => row.sgst, width: '105px', right: true, cell: row => <span className="gst-tax-value sgst">{formatAmount(row.sgst)}</span> },
  { name: 'Original Invoice (ref.)', selector: row => row.originalInvoice, width: '180px', cell: row => <span className="gst-mono gst-muted-value">{displayValue(row.originalInvoice)}</span> },
];

const hsn12Columns = [
  { name: 'Sr', selector: row => row.sr, width: '64px', cell: row => <span className="gst-row-number">{row.sr}</span> },
  { name: 'Supplier State', selector: row => row.supplierState, width: '150px', sortable: true },
  { name: 'State Code', selector: row => row.stateCode, width: '100px', cell: row => <span className="gst-code-badge">{displayValue(row.stateCode)}</span> },
  { name: 'HSN/SAC', selector: row => row.hsnSac, width: '125px', sortable: true, cell: row => <span className="gst-mono gst-voucher">{displayValue(row.hsnSac)}</span> },
  { name: 'Description', selector: row => row.description, width: '300px', cell: row => <span className="gst-description" title={row.description}>{displayValue(row.description)}</span> },
  { name: 'UQC', selector: row => row.uqc, width: '85px', cell: row => <span className="gst-static-badge">{displayValue(row.uqc)}</span> },
  { name: 'Total Quantity', selector: row => row.totalQuantity, width: '125px', right: true, cell: row => <span className="gst-number-cell">{displayValue(row.totalQuantity)}</span> },
  { name: 'Taxable Value', selector: row => row.taxableValue, width: '145px', right: true, cell: row => <span className="gst-money emphasized">{formatAmount(row.taxableValue)}</span> },
  { name: 'IGST', selector: row => row.igst, width: '115px', right: true, cell: row => <span className="gst-tax-value igst">{formatAmount(row.igst)}</span> },
  { name: 'CGST', selector: row => row.cgst, width: '115px', right: true, cell: row => <span className="gst-tax-value cgst">{formatAmount(row.cgst)}</span> },
  { name: 'SGST', selector: row => row.sgst, width: '115px', right: true, cell: row => <span className="gst-tax-value sgst">{formatAmount(row.sgst)}</span> },
  { name: 'Cess', selector: row => row.cess, width: '100px', right: true, cell: row => <span className="gst-money">{formatAmount(row.cess)}</span> },
];

const docs13Columns = [
  { name: 'Sr', selector: row => row.sr, width: '64px', cell: row => <span className="gst-row-number">{row.sr}</span> },
  { name: 'Supplier State', selector: row => row.supplierState, width: '150px', sortable: true },
  { name: 'Nature of Document', selector: row => row.natureOfDocument, width: '195px', cell: row => <span className={`gst-doc-badge ${row.natureOfDocument === 'Credit Note' ? 'credit' : 'invoice'}`}>{displayValue(row.natureOfDocument)}</span> },
  { name: 'Series', selector: row => row.series, width: '155px', sortable: true, cell: row => <span className="gst-mono gst-voucher">{displayValue(row.series)}</span> },
  { name: 'Sr. No. From', selector: row => row.serialNumberFrom, width: '180px', cell: row => <span className="gst-mono">{displayValue(row.serialNumberFrom)}</span> },
  { name: 'Sr. No. To', selector: row => row.serialNumberTo, width: '180px', cell: row => <span className="gst-mono">{displayValue(row.serialNumberTo)}</span> },
  { name: 'Total Number', selector: row => row.totalNumber, width: '115px', right: true, cell: row => <span className="gst-number-cell">{displayValue(row.totalNumber)}</span> },
  { name: 'Cancelled', selector: row => row.cancelled, width: '105px', right: true, cell: row => <span className="gst-number-cell">{displayValue(row.cancelled)}</span> },
  { name: 'Net Issued', selector: row => row.netIssued, width: '105px', right: true, cell: row => <span className="gst-number-cell">{displayValue(row.netIssued)}</span> },
  { name: 'Numbers Missing in Register', selector: row => row.missingNumbers, width: '350px', cell: row => <span className="gst-mono gst-muted-value" title={row.missingNumbers}>{displayValue(row.missingNumbers)}</span> },
];

const b2bSezDeColumns = [
  { name: 'Sr', selector: row => row.sr, width: '64px', cell: row => <span className="gst-row-number">{row.sr}</span> },
  { name: 'GSTIN/UIN of Recipient', selector: row => row.recipientGstin, width: '185px', cell: row => <span className="gst-mono">{displayValue(row.recipientGstin)}</span> },
  { name: 'Receiver Name', selector: row => row.receiverName, width: '210px', cell: row => <span className="gst-party-name" title={row.receiverName}>{displayValue(row.receiverName)}</span> },
  { name: 'Invoice Number', selector: row => row.invoiceNumber, width: '180px', sortable: true, cell: row => <span className="gst-mono gst-voucher">{displayValue(row.invoiceNumber)}</span> },
  { name: 'Invoice Date', selector: row => row.invoiceDate, width: '115px', sortable: true },
  { name: 'Invoice Value', selector: row => row.invoiceValue, width: '135px', right: true, cell: row => <span className="gst-money emphasized">{formatAmount(row.invoiceValue)}</span> },
  { name: 'Place of Supply', selector: row => row.placeOfSupply, width: '150px', cell: row => <span className="gst-code-badge">{displayValue(row.placeOfSupply)}</span> },
  { name: 'Reverse Charge', selector: row => row.reverseCharge, width: '120px', cell: row => <span className="gst-static-badge">{displayValue(row.reverseCharge)}</span> },
  { name: 'Applicable % of Tax Rate', selector: row => row.applicableTaxRate, width: '170px', right: true, cell: row => displayBlank(row.applicableTaxRate) },
  { name: 'Invoice Type', selector: row => row.invoiceType, width: '145px', cell: row => <span className="gst-doc-badge invoice">{displayValue(row.invoiceType)}</span> },
  { name: 'E-Commerce GSTIN', selector: row => row.ecommerceGstin, width: '160px', cell: row => <span className="gst-mono gst-muted-value">{displayBlank(row.ecommerceGstin)}</span> },
  { name: 'Rate', selector: row => row.rate, width: '80px', right: true, cell: row => <span className="gst-rate">{row.rate === null || row.rate === undefined ? '—' : `${row.rate}%`}</span> },
  { name: 'Taxable Value', selector: row => row.taxableValue, width: '140px', right: true, cell: row => <span className="gst-money emphasized">{formatAmount(row.taxableValue)}</span> },
  { name: 'Integrated Tax', selector: row => row.integratedTax, width: '125px', right: true, cell: row => <span className="gst-tax-value igst">{formatAmount(row.integratedTax)}</span> },
  { name: 'Central Tax', selector: row => row.centralTax, width: '115px', right: true, cell: row => <span className="gst-tax-value cgst">{formatAmount(row.centralTax)}</span> },
  { name: 'State/UT Tax', selector: row => row.stateUtTax, width: '120px', right: true, cell: row => <span className="gst-tax-value sgst">{formatAmount(row.stateUtTax)}</span> },
  { name: 'Cess Amount', selector: row => row.cessAmount, width: '115px', right: true, cell: row => <span className="gst-money">{formatAmount(row.cessAmount)}</span> },
  { name: 'IRN', selector: row => row.irn, width: '250px', cell: row => <span className="gst-mono gst-key" title={row.irn}>{displayBlank(row.irn)}</span> },
  { name: 'IRN Date', selector: row => row.irnDate, width: '110px', cell: row => displayBlank(row.irnDate) },
  { name: 'E-Invoice Status', selector: row => row.eInvoiceStatus, width: '145px', cell: row => <span className="gst-muted-value">{displayBlank(row.eInvoiceStatus)}</span> },
  { name: 'GSTR-1 Auto-Population / Deletion Date', selector: row => row.autoPopulationDate, width: '245px', cell: row => <span className="gst-muted-value">{displayBlank(row.autoPopulationDate)}</span> },
  { name: 'GSTR-1 Auto-Population / Deletion Status', selector: row => row.autoPopulationStatus, width: '255px', cell: row => <span className="gst-muted-value">{displayBlank(row.autoPopulationStatus)}</span> },
  { name: 'Auto-Population / Deletion Error', selector: row => row.autoPopulationError, width: '250px', cell: row => <span className="gst-muted-value" title={row.autoPopulationError}>{displayBlank(row.autoPopulationError)}</span> },
];

const TAB_REQUESTS = {
  details: getGstDetails,
  b2b: getGstB2B,
  cdnr: getGstCDNR,
  hsn12: getGstHSN12,
  docs13: getGstDocs13,
  b2bSezDe: getGstB2BSezDe,
};

const TAB_COLUMNS = {
  details: detailColumns,
  b2b: b2bColumns,
  cdnr: cdnrColumns,
  hsn12: hsn12Columns,
  docs13: docs13Columns,
  b2bSezDe: b2bSezDeColumns,
};

const TAB_LABELS = {
  details: 'Details',
  b2b: 'B2B',
  cdnr: 'CDNR 9B',
  hsn12: 'HSN 12',
  docs13: 'Docs 13',
  b2bSezDe: 'B2B, SEZ, DE',
};

const csvCell = value => {
  if (value === null || value === undefined) return '""';
  const original = typeof value === 'object' ? JSON.stringify(value) : String(value);
  const safe = typeof value === 'number' || !/^[=+\-@]/.test(original)
    ? original
    : `'${original}`;
  return `"${safe.replace(/"/g, '""')}"`;
};

const GstDetails = () => {
  const [rows, setRows] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [totalRows, setTotalRows] = useState(0);
  const [commonFilters, setCommonFilters] = useState({ ...COMMON_FILTER_DEFAULTS });
  const [appliedCommonFilters, setAppliedCommonFilters] = useState({ ...COMMON_FILTER_DEFAULTS });
  const [tabFilters, setTabFilters] = useState(createTabFilters);
  const [appliedTabFilters, setAppliedTabFilters] = useState(createTabFilters);
  const appliedFilters = useMemo(() => ({
    ...appliedCommonFilters,
    ...appliedTabFilters[activeTab],
  }), [activeTab, appliedCommonFilters, appliedTabFilters]);

  useEffect(() => {
    let active = true;

    const loadDetails = async () => {
      setLoading(true);
      setError('');
      try {
        const request = TAB_REQUESTS[activeTab];
        const response = await request({ ...appliedFilters, page, limit });
        if (!active) return;
        setRows(response?.data?.rows || []);
        setTotalRows(response?.data?.pagination?.total || 0);
      } catch (requestError) {
        if (!active) return;
        setRows([]);
        setTotalRows(0);
        const tabName = TAB_LABELS[activeTab];
        setError(requestError.message || `Failed to load GST ${tabName}`);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadDetails();
    return () => {
      active = false;
    };
  }, [activeTab, page, limit, appliedFilters]);

  const handleTabChange = nextTab => {
    if (nextTab === activeTab) return;
    setActiveTab(nextTab);
    setPage(1);
    setRows([]);
    setTotalRows(0);
    setExportError('');
  };

  const handleFilterChange = event => {
    const { name, value } = event.target;
    if (name === 'date_from' || name === 'date_to') {
      setCommonFilters(current => ({ ...current, [name]: value }));
      return;
    }
    setTabFilters(current => ({
      ...current,
      [activeTab]: { ...current[activeTab], [name]: value },
    }));
  };

  const handleFilterSubmit = event => {
    event.preventDefault();
    setExportError('');
    setPage(1);
    setAppliedCommonFilters({ ...commonFilters });
    setAppliedTabFilters(current => ({
      ...current,
      [activeTab]: { ...tabFilters[activeTab] },
    }));
  };

  const handleClearFilters = () => {
    setExportError('');
    setCommonFilters({ ...COMMON_FILTER_DEFAULTS });
    setAppliedCommonFilters({ ...COMMON_FILTER_DEFAULTS });
    setTabFilters(current => ({
      ...current,
      [activeTab]: { ...TAB_FILTER_DEFAULTS[activeTab] },
    }));
    setAppliedTabFilters(current => ({
      ...current,
      [activeTab]: { ...TAB_FILTER_DEFAULTS[activeTab] },
    }));
    setPage(1);
  };

  const handleExportCsv = async () => {
    const exportTab = activeTab;
    const request = TAB_REQUESTS[exportTab];
    const columns = TAB_COLUMNS[exportTab];
    const filters = { ...appliedFilters };
    const exportPageSize = 500;

    setExporting(true);
    setExportError('');

    try {
      const firstResponse = await request({ ...filters, page: 1, limit: exportPageSize });
      const firstResult = firstResponse?.data || {};
      const exportRows = [...(firstResult.rows || [])];
      const totalPages = firstResult.pagination?.totalPages || 1;
      const exportConcurrency = 4;

      for (let firstPage = 2; firstPage <= totalPages; firstPage += exportConcurrency) {
        const pageNumbers = Array.from(
          { length: Math.min(exportConcurrency, totalPages - firstPage + 1) },
          (_, index) => firstPage + index
        );
        const responses = await Promise.all(
          pageNumbers.map(nextPage => request({
            ...filters,
            page: nextPage,
            limit: exportPageSize,
          }))
        );
        responses.forEach(response => exportRows.push(...(response?.data?.rows || [])));
      }

      const header = columns.map(column => csvCell(column.name)).join(',');
      const csvRows = exportRows.map(row => columns
        .map(column => csvCell(column.selector ? column.selector(row) : ''))
        .join(','));
      const csv = `\uFEFF${[header, ...csvRows].join('\r\n')}`;
      const tabName = TAB_LABELS[exportTab].toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const date = new Date().toISOString().slice(0, 10);

      await saveBlob(
        new Blob([csv], { type: 'text/csv;charset=utf-8' }),
        `gst-${tabName}-${date}.csv`
      );
    } catch (exportRequestError) {
      setExportError(exportRequestError.message || 'Unable to export the filtered GST data.');
    } finally {
      setExporting(false);
    }
  };

  const activeTabName = TAB_LABELS[activeTab];
  const activeColumns = TAB_COLUMNS[activeTab];
  const activeFilterConfig = TAB_FILTER_CONFIG[activeTab];
  const activeFilterValues = tabFilters[activeTab];
  const hasFilterValues = Object.values(commonFilters).some(Boolean) ||
    Object.values(activeFilterValues).some(Boolean) ||
    Object.values(appliedCommonFilters).some(Boolean) ||
    Object.values(appliedTabFilters[activeTab]).some(Boolean);

  return (
    <div className="container gst-page">
      <form className="gst-filter-panel" onSubmit={handleFilterSubmit}>
        <div className={`gst-filter-fields${activeFilterConfig.length === 1 ? ' compact' : ''}`}>
          {activeFilterConfig.map(field => (
            <label key={field.name}>
              {field.label}
              {field.type === 'select' ? (
                <select name={field.name} value={activeFilterValues[field.name]} onChange={handleFilterChange}>
                  {field.options.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.type || 'text'}
                  name={field.name}
                  value={activeFilterValues[field.name]}
                  onChange={handleFilterChange}
                  placeholder={field.placeholder}
                  min={field.type === 'number' ? '0' : undefined}
                  step={field.type === 'number' ? '0.01' : undefined}
                />
              )}
            </label>
          ))}
          <label>
            From date (all tabs)
            <input type="date" name="date_from" value={commonFilters.date_from} max={commonFilters.date_to || undefined} onChange={handleFilterChange} />
          </label>
          <label>
            To date (all tabs)
            <input type="date" name="date_to" value={commonFilters.date_to} min={commonFilters.date_from || undefined} onChange={handleFilterChange} />
          </label>
          <div className="gst-filter-actions">
            <button type="submit" className="gst-apply-button" disabled={loading}>Apply filters</button>
            <button type="button" className="gst-clear-button" onClick={handleClearFilters} disabled={!hasFilterValues}>Clear</button>
            <button
              type="button"
              className="gst-export-button"
              onClick={handleExportCsv}
              disabled={loading || exporting || totalRows === 0}
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
        </div>
        {exportError && <p className="gst-export-error" role="alert">{exportError}</p>}
      </form>

      <div className="gst-details-card">
        <div className="gst-tab-bar">
          <div className="gst-tabs" role="tablist" aria-label="GST return sheets">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'details'}
              className={`gst-tab-button${activeTab === 'details' ? ' active' : ''}`}
              onClick={() => handleTabChange('details')}
            >
              <span className="gst-tab-mark" />
              Details
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'b2b'}
              className={`gst-tab-button${activeTab === 'b2b' ? ' active' : ''}`}
              onClick={() => handleTabChange('b2b')}
            >
              <span className="gst-tab-mark" />
              B2B
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'cdnr'}
              className={`gst-tab-button${activeTab === 'cdnr' ? ' active' : ''}`}
              onClick={() => handleTabChange('cdnr')}
            >
              <span className="gst-tab-mark" />
              CDNR 9B
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'hsn12'}
              className={`gst-tab-button${activeTab === 'hsn12' ? ' active' : ''}`}
              onClick={() => handleTabChange('hsn12')}
            >
              <span className="gst-tab-mark" />
              HSN 12
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'docs13'}
              className={`gst-tab-button${activeTab === 'docs13' ? ' active' : ''}`}
              onClick={() => handleTabChange('docs13')}
            >
              <span className="gst-tab-mark" />
              Docs 13
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'b2bSezDe'}
              className={`gst-tab-button${activeTab === 'b2bSezDe' ? ' active' : ''}`}
              onClick={() => handleTabChange('b2bSezDe')}
            >
              <span className="gst-tab-mark" />
              B2B, SEZ, DE
            </button>
          </div>
          <div className="gst-row-caption">
            {loading ? 'Refreshing…' : `${totalRows.toLocaleString('en-IN')} ${activeTab === 'details' ? 'lines' : activeTab === 'hsn12' ? 'summary rows' : activeTab === 'docs13' ? 'series' : 'rate rows'} found`}
          </div>
        </div>

        <div className="gst-table-wrap">
          <DataTable
            columns={activeColumns}
            data={rows}
            forceDesktop
            progressPending={loading}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationDefaultPage={page}
            paginationPerPage={limit}
            paginationRowsPerPageOptions={[50, 100, 200, 500]}
            onChangePage={nextPage => setPage(nextPage)}
            onChangeRowsPerPage={nextLimit => {
              setLimit(nextLimit);
              setPage(1);
            }}
            noDataComponent={(
              <div className={`gst-empty-state${error ? ' error' : ''}`}>
                <span>{error ? '!' : 'GST'}</span>
                <strong>{error ? `Unable to load GST ${activeTabName}` : `No GST ${activeTabName} entries found`}</strong>
                <p>{error || 'Try changing the filters or check again after invoices are generated.'}</p>
              </div>
            )}
            customStyles={{
              headRow: {
                style: {
                  minHeight: '50px',
                  background: '#f6f8fc',
                  borderBottom: '1px solid #dce3ee',
                },
              },
              headCells: {
                style: {
                  color: '#536176',
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.055em',
                  textTransform: 'uppercase',
                },
              },
              rows: {
                style: {
                  minHeight: '54px',
                  borderBottom: '1px solid #edf1f6',
                  fontSize: '12px',
                  color: '#243247',
                },
                highlightOnHoverStyle: {
                  backgroundColor: '#f5f8ff',
                  borderBottomColor: '#dfe8f7',
                  outline: 'none',
                },
              },
              pagination: {
                style: {
                  borderTop: '1px solid #e7ebf1',
                  color: '#536176',
                },
              },
            }}
            highlightOnHover
          />
        </div>
      </div>
    </div>
  );
};

export default GstDetails;
